/* divain: el contador usa el horario de la tienda, no el del dispositivo. Ver PERSONALIZACIONES-PEBBLE.md */
import { Component } from "@theme/component";

class CountdownTimer extends Component {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();

    this.init();
  }

  init() {
    this.MS_IN_SEC = 1000;
    this.MS_IN_MIN = 60 * this.MS_IN_SEC;
    this.MS_IN_HRS = 60 * this.MS_IN_MIN;
    this.MS_IN_DAY = 24 * this.MS_IN_HRS;

    this.units = ["day", "hour", "minute", "second"];
    this.type = this.dataset.type;
    this.duration = this.dataset.duration;
    this.shopOffsetMinutes = this.parseShopOffset(this.dataset.shopUtcOffset);
    this.loop = false;
    this.shouldAddLeadingZero = true;

    this.initCountdown();
  }

  initCountdown() {
    switch (this.type) {
      case "fixed_time":
        const countdownData = this.dataset.dueDate;
        if (countdownData) {
          this.targetTime = this.parseFixedTime(countdownData);
        }
        break;
      default: // Evergreen.
        this.targetTime = this.getEvergreenDueDate();
        break;
    }

    if (!this.targetTime) {
      this.terminateCountdown();
      return;
    }

    this.startTime = Date.now();
    if (this.targetTime > this.startTime) {
      this.beginCountdown();
    } else {
      this.terminateCountdown();
    }
  }

  beginCountdown() {
    clearInterval(this.intervalId);
    this.updateDisplay(this.targetTime - Date.now());
    this.intervalId = setInterval(() => {
      const remainingTime = this.targetTime - Date.now();
      if (remainingTime <= 0) {
        this.terminateCountdown();
      } else {
        this.updateDisplay(remainingTime);
      }
    }, 1000);
  }

  updateDisplay(remainingTime) {
    const timeComponents = this.computeTimeComponents(Math.max(0, remainingTime));
    this.units.forEach((unit) => {
      if (this.refs[unit]) {
        this.refs[unit].textContent = this.formatTimeComponent(timeComponents[unit]);
      }
    });
    this.setAttribute('data-ready', 'true');
  }

  disconnectedCallback() {
    clearInterval(this.intervalId);
    super.disconnectedCallback?.();
  }

  computeTimeComponents(ms) {
    return {
      day: Math.floor(ms / this.MS_IN_DAY),
      hour: Math.floor((ms % this.MS_IN_DAY) / this.MS_IN_HRS),
      minute: Math.floor((ms % this.MS_IN_HRS) / this.MS_IN_MIN),
      second: Math.floor((ms % this.MS_IN_MIN) / this.MS_IN_SEC),
    };
  }

  formatTimeComponent(value) {
    return this.shouldAddLeadingZero && value < 10 ? `0${value}` : value.toString();
  }

  terminateCountdown() {
    clearInterval(this.intervalId);
    if (this.loop) {
      this.beginCountdown();
    } else {
      if (Shopify.designMode) return;

      this.classList.remove("flex");
      this.classList.add("hidden");
    }
  }

  resetCountdown() {
    clearInterval(this.intervalId);
    this.units.forEach((unit) => {
      if (this.refs[unit]) {
        this.refs[unit].textContent = "00";
      }
    });
  }

  /*
    Horario de la tienda, no del dispositivo.

    El contador original usaba `new Date()` y `getHours()`/`setHours()`, que son
    la hora LOCAL de quien mira la web: un "cada día" terminaba a medianoche de
    México para quien estuviera en México y a medianoche de España para quien
    estuviera aquí. La promoción debe acabar a la vez para todo el mundo.

    Shopify no expone la zona horaria de la tienda en Liquid, pero el filtro
    `date` sí la usa, así que el snippet pinta el desfase con `%z` y aquí se
    trabaja con la hora de pared de la tienda. Si el atributo falta o viene mal,
    se vuelve al comportamiento anterior en vez de romper el contador.

    Límite conocido: el desfase es el del momento de pintar la página. Si una
    página se quedara cacheada cruzando el cambio de hora, el contador iría una
    hora desplazado hasta que se vuelva a pedir.
  */
  parseShopOffset(raw) {
    const match = typeof raw === "string" && raw.trim().match(/^([+-])(\d{2}):?(\d{2})$/);
    if (!match) return null;
    const sign = match[1] === "-" ? -1 : 1;
    return sign * (parseInt(match[2], 10) * 60 + parseInt(match[3], 10));
  }

  /* "Ahora" en hora de pared de la tienda. Con desfase se desplaza el instante
     y se lee con los getters UTC, que es la forma de leer la hora de otra zona
     sin depender de la del navegador. */
  shopNowParts() {
    if (this.shopOffsetMinutes === null) {
      const local = new Date();
      return {
        year: local.getFullYear(),
        month: local.getMonth(),
        day: local.getDate(),
        hours: local.getHours(),
        minutes: local.getMinutes(),
        seconds: local.getSeconds(),
        weekday: local.getDay(),
      };
    }

    const shifted = new Date(Date.now() + this.shopOffsetMinutes * this.MS_IN_MIN);
    return {
      year: shifted.getUTCFullYear(),
      month: shifted.getUTCMonth(),
      day: shifted.getUTCDate(),
      hours: shifted.getUTCHours(),
      minutes: shifted.getUTCMinutes(),
      seconds: shifted.getUTCSeconds(),
      weekday: shifted.getUTCDay(),
    };
  }

  /* Pasa una hora de pared de la tienda al instante real. `Date.UTC` ya
     normaliza los desbordamientos (mes 12, día 32...), así que no hace falta
     tratar los finales de mes aparte. */
  shopWallToEpoch(year, month, day, hours, minutes, seconds) {
    if (this.shopOffsetMinutes === null) {
      return new Date(year, month, day, hours, minutes, seconds, 0).getTime();
    }
    return Date.UTC(year, month, day, hours, minutes, seconds, 0) - this.shopOffsetMinutes * this.MS_IN_MIN;
  }

  /* La fecha fija se escribe en el editor sin zona horaria ("2026/08/20 03:45 PM"),
     así que se entiende como hora de la tienda. `new Date(cadena)` la habría
     interpretado en la del dispositivo. */
  parseFixedTime(raw) {
    const match = String(raw)
      .trim()
      .match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})(?:[ T]+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?)?$/i);

    if (!match) return new Date(raw).getTime();

    let hours = match[4] ? parseInt(match[4], 10) : 0;
    const meridiem = match[7] && match[7].toUpperCase();
    if (meridiem === "PM" && hours < 12) hours += 12;
    if (meridiem === "AM" && hours === 12) hours = 0;

    return this.shopWallToEpoch(
      parseInt(match[1], 10),
      parseInt(match[2], 10) - 1,
      parseInt(match[3], 10),
      hours,
      match[5] ? parseInt(match[5], 10) : 0,
      match[6] ? parseInt(match[6], 10) : 0
    );
  }

  getEvergreenDueDate() {
    const now = this.shopNowParts();
    let intervalHrs;

    switch (this.duration) {
      case "every_month":
        // Primer día del mes siguiente, a las 00:00 de la tienda.
        return this.shopWallToEpoch(now.year, now.month + 1, 1, 0, 0, 0);
      case "every_week":
        // Próximo domingo a las 00:00 de la tienda.
        return this.shopWallToEpoch(now.year, now.month, now.day + (7 - now.weekday), 0, 0, 0);
      case "every_2_hrs":
        intervalHrs = 2;
        break;
      case "every_6_hrs":
        intervalHrs = 6;
        break;
      case "every_12_hrs":
        intervalHrs = 12;
        break;
      case "every_day":
        intervalHrs = 24;
        break;
    }

    // Puntos de corte del día según el intervalo. Con "cada día" solo hay uno:
    // las 00:00. El campo "Hora de fin" del editor no se usa en modo evergreen.
    const startPoints = [];
    for (let i = 0; i < 24; i += intervalHrs) {
      startPoints.push(i);
    }

    for (let i = 0; i < startPoints.length; i++) {
      if (
        now.hours < startPoints[i] ||
        (now.hours === startPoints[i] && now.minutes === 0 && now.seconds === 0)
      ) {
        return this.shopWallToEpoch(now.year, now.month, now.day, startPoints[i], 0, 0);
      }
    }

    // Si hoy ya no queda ninguno, el primero de mañana.
    return this.shopWallToEpoch(now.year, now.month, now.day + 1, startPoints[0], 0, 0);
  }
}

if (!customElements.get("countdown-timer")) {
  customElements.define("countdown-timer", CountdownTimer);
}
